import React from 'react';
import { Link } from 'react-router-dom';
import { Barcode } from '@phosphor-icons/react';

const LegalShell = ({ title, children, testId }) => (
  <div className="min-h-screen bg-background" data-testid={testId}>
    <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <div className="w-9 h-9 bg-brand flex items-center justify-center">
          <Barcode weight="bold" className="text-white" size={20} />
        </div>
        <span className="font-bold text-lg tracking-tight">STOCKARIS</span>
      </Link>
      <nav className="flex gap-4 text-xs uppercase tracking-widest font-bold text-muted-foreground">
        <Link to="/legal/terms" className="hover:text-brand">Terms</Link>
        <Link to="/legal/privacy" className="hover:text-brand">Privacy</Link>
        <Link to="/legal/sla" className="hover:text-brand">SLA</Link>
        <Link to="/login" className="hover:text-brand">Sign in</Link>
      </nav>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold mb-6">{title}</h1>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-8 font-mono">Last updated: 21 April 2026 · Draft for review — not legal advice</p>
      <div className="prose prose-slate max-w-none text-sm leading-relaxed text-foreground space-y-4">{children}</div>
      <p className="mt-12 text-xs text-muted-foreground font-mono">
        Questions? <a href="mailto:legal@stockaris.com" className="text-brand underline">legal@stockaris.com</a>
      </p>
    </main>
  </div>
);

export function Terms() {
  return (
    <LegalShell title="Terms of Service" testId="legal-terms">
      <h2>1. Acceptance</h2>
      <p>By creating a Stockaris account you agree to these Terms and acknowledge our <Link to="/legal/privacy" className="text-brand underline">Privacy Policy</Link>. If you are accepting on behalf of a company, you represent that you have authority to bind that company.</p>
      <h2>2. The Service</h2>
      <p>Stockaris provides a cloud-based Warehouse Management System (WMS). We grant you a non-exclusive, non-transferable licence to use the Service during your subscription.</p>
      <h2>3. Subscription Activation</h2>
      <p>New customers select a paid plan at signup and complete payment via Stripe Checkout before the account is fully activated. Customers who'd like to evaluate the platform first can request a personalised demo at <Link to="/request-demo" className="text-brand underline">/request-demo</Link>; a representative will follow up within one business day. No free trial is offered.</p>
      <h2>4. Fees & Billing</h2>
      <p>Paid subscriptions are billed monthly in advance via Stripe. You may upgrade, downgrade, or cancel at any time from the Billing page. Refunds are issued at our discretion and generally pro-rated for unused whole months.</p>
      <h2>5. Your Data</h2>
      <p>You retain ownership of all data you upload. We act as a data processor under the Australian Privacy Act 1988 (Privacy Act) and the Australian Privacy Principles (APPs). You may export your data at any time via the in-app export tool.</p>
      <h2>6. Acceptable Use</h2>
      <p>You agree not to: (a) attempt to circumvent security; (b) upload malicious code; (c) use the Service to violate any law; (d) resell access without written consent.</p>
      <h2>7. Uptime & Availability</h2>
      <p>We target 99.5% monthly uptime for Pro and Enterprise tiers. Enterprise customers receive a written SLA with service credits — see the <Link to="/legal/sla" className="text-brand underline">SLA page</Link>.</p>
      <h2>8. Liability</h2>
      <p>To the extent permitted by law, Stockaris's total liability for any claim is capped at fees paid in the prior 12 months. We are not liable for indirect, consequential, or incidental damages.</p>
      <h2>9. Termination</h2>
      <p>We may suspend or terminate accounts that breach these Terms. On termination we will provide a 30-day data export window before permanent deletion.</p>
      <h2>10. Governing Law</h2>
      <p>These Terms are governed by the laws of New South Wales, Australia. Any dispute must be filed in the courts of Sydney, NSW.</p>
      <h2>11. Changes</h2>
      <p>We may update these Terms. Material changes will be notified by email at least 14 days before taking effect.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" testId="legal-privacy">
      <p>Stockaris Pty Ltd ("we", "us") complies with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs). This policy explains what data we collect and how we use it.</p>
      <h2>1. Data we collect</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Account data</strong> — name, work email, company details, authentication tokens.</li>
        <li><strong>Operational data</strong> — inventory, orders, shipments, KPIs (your company's own records).</li>
        <li><strong>Usage data</strong> — pages viewed, API calls, feature usage for product improvement.</li>
        <li><strong>Support data</strong> — messages you send to support@stockaris.com.</li>
      </ul>
      <h2>2. How we use it</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li>To provide, secure, and improve the Service.</li>
        <li>To send service and billing notifications.</li>
        <li>To detect abuse and comply with legal obligations.</li>
        <li>We <strong>do not sell</strong> personal information.</li>
      </ul>
      <h2>3. Where your data lives</h2>
      <p>Customer data is stored in secured cloud infrastructure. Backups are encrypted at rest.</p>
      <h2>4. Your rights (APP 12)</h2>
      <p>You may access, correct, or export your personal information at any time. Enterprise customers can trigger a full ZIP export of company data via <strong>Compliance → Export</strong>. To delete an account, email <a href="mailto:privacy@stockaris.com" className="text-brand underline">privacy@stockaris.com</a>.</p>
      <h2>5. Data retention</h2>
      <p>We retain transactional records for up to 7 years to satisfy Australian tax and corporate-records requirements. Non-transactional data is deleted within 30 days of account closure.</p>
      <h2>6. Security</h2>
      <p>We employ industry-standard controls: TLS in transit, encryption at rest, role-based access, 2FA for admins, SAML SSO for Enterprise, audit logging, and annual penetration testing.</p>
      <h2>7. Breach notification</h2>
      <p>We notify the OAIC and affected customers within 72 hours of discovering any eligible data breach under the Notifiable Data Breaches scheme.</p>
      <h2>8. Contact</h2>
      <p>Privacy queries: <a href="mailto:privacy@stockaris.com" className="text-brand underline">privacy@stockaris.com</a>.</p>
    </LegalShell>
  );
}

export function SLA() {
  return (
    <LegalShell title="Service Level Agreement" testId="legal-sla">
      <h2>Uptime commitment</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Starter / Free Trial</strong> — best-effort, no SLA.</li>
        <li><strong>Pro</strong> — 99.5% monthly uptime target.</li>
        <li><strong>Enterprise</strong> — 99.9% monthly uptime, 15-minute P1 response, 1-hour P1 resolution target.</li>
      </ul>
      <h2>Service credits (Enterprise)</h2>
      <table className="text-sm mt-2 border border-border">
        <thead className="bg-muted">
          <tr><th className="p-2 border border-border text-left">Monthly uptime</th><th className="p-2 border border-border text-left">Credit</th></tr>
        </thead>
        <tbody>
          <tr><td className="p-2 border border-border">&lt; 99.9% but ≥ 99.0%</td><td className="p-2 border border-border">10% of monthly fee</td></tr>
          <tr><td className="p-2 border border-border">&lt; 99.0% but ≥ 95.0%</td><td className="p-2 border border-border">25% of monthly fee</td></tr>
          <tr><td className="p-2 border border-border">&lt; 95.0%</td><td className="p-2 border border-border">50% of monthly fee</td></tr>
        </tbody>
      </table>
      <h2>Exclusions</h2>
      <p>Scheduled maintenance (announced 48h+ ahead), force majeure events, customer-network issues, and third-party outages beyond our reasonable control are excluded.</p>
      <h2>Support channels</h2>
      <ul className="list-disc ml-5 space-y-1">
        <li>Email: <a href="mailto:support@stockaris.com" className="text-brand underline">support@stockaris.com</a> (all tiers)</li>
        <li>In-app chat (Pro + Enterprise)</li>
        <li>Dedicated Slack channel + account manager (Enterprise)</li>
      </ul>
    </LegalShell>
  );
}
